import React, { useRef, useEffect, useState } from 'react';
import ReactSelect from 'react-select';
import type { Props, GroupBase } from 'react-select';
import { reactSelectStyles } from '../../utils/reactSelectTheme';

interface SelectExtraProps {
  /**
   * When true (default), measures all options and locks the control width
   * to the widest one. Set to false for paginated/async dropdowns where
   * all options aren't known upfront.
   */
  autoSize?: boolean;
}

/**
 * Wrapper around react-select that:
 * 1. Applies Bootstrap theme-aware styles by default
 * 2. When autoSize=true (default), sizes control to the widest option
 *
 * Use this instead of importing react-select directly.
 */
function Select<
  Option = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(props: Props<Option, IsMulti, Group> & SelectExtraProps & { ref?: React.Ref<any> }) {
  const { styles, options, formatOptionLabel, placeholder, autoSize = true, ref, ...rest } = props;
  const contentSizerRef = useRef<HTMLDivElement>(null);
  const [lockedWidth, setLockedWidth] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (!autoSize) return;
    const el = contentSizerRef.current;
    if (!el) return;

    // Double rAF to ensure styles/fonts are applied
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        let widestContent = 0;
        for (let i = 0; i < el.children.length; i++) {
          const child = el.children[i] as HTMLElement;
          const w = child.scrollWidth;
          if (w > widestContent) widestContent = w;
        }
        if (widestContent > 0) {
          // Control layout:
          //   [8px padding] [content] [8px padding] [separator 1px + 16px margin] [dropdown chevron 38px]
          //   If clearable: + [clear X 28px]
          const indicatorsWidth = rest.isClearable ? 91 : 55;
          setLockedWidth(widestContent + indicatorsWidth);
        }
      });
    });
  }, [autoSize, options, formatOptionLabel, placeholder, rest.isClearable]);

  const mergedStyles = React.useMemo(() => {
    const base = { ...reactSelectStyles, ...styles };

    if (!autoSize || lockedWidth === undefined) return base;

    const userControl = base.control;
    const userMenu = base.menu;
    const userSingleValue = base.singleValue;

    return {
      ...base,
      control: (baseStyle: any, state: any) => {
        const orig = typeof userControl === 'function' ? userControl(baseStyle, state) : baseStyle;
        return { ...orig, width: lockedWidth, minWidth: lockedWidth, flexShrink: 0 };
      },
      menu: (baseStyle: any, state: any) => {
        const orig = typeof userMenu === 'function' ? userMenu(baseStyle, state) : baseStyle;
        return { ...orig, width: lockedWidth };
      },
      singleValue: (baseStyle: any, state: any) => {
        const orig = typeof userSingleValue === 'function' ? userSingleValue(baseStyle, state) : baseStyle;
        return { ...orig, maxWidth: 'none', overflow: 'visible', position: 'relative' };
      },
    };
  }, [autoSize, styles, lockedWidth]);

  return (
    <>
      {autoSize && (
        <div
          aria-hidden
          style={{
            position: 'fixed',
            top: -9999,
            left: -9999,
            visibility: 'hidden',
            pointerEvents: 'none',
            fontSize: 'inherit',
            fontFamily: 'inherit',
          }}
        >
          <div ref={contentSizerRef}>
            <div style={{ display: 'inline-block', whiteSpace: 'nowrap', padding: '2px 0' }}>
              {typeof placeholder === 'string' ? placeholder : 'Select...'}
            </div>
            {(options as Option[] | undefined)?.map((option, i) => (
              <div key={i} style={{ display: 'inline-block', whiteSpace: 'nowrap', padding: '2px 0' }}>
                {formatOptionLabel
                  ? formatOptionLabel(option, { context: 'value', inputValue: '', selectValue: [] })
                  : String((option as any).label ?? '')}
              </div>
            ))}
          </div>
        </div>
      )}
      <ReactSelect<Option, IsMulti, Group>
        ref={ref}
        options={options}
        formatOptionLabel={formatOptionLabel}
        placeholder={placeholder}
        styles={mergedStyles as any}
        {...rest}
      />
    </>
  );
}

export default Select;
